import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FieldType } from '@ngx-formly/core';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'irembogov-paginated-template-dropdown',
  template: `
    <ng-container *ngIf="refreshable; else plainSelect">
      <div class="iwb-template-select-row">
        <ng-container *ngTemplateOutlet="selectTemplate"></ng-container>
        <button
          type="button"
          class="iwb-template-refresh"
          [disabled]="loading"
          title="Refresh"
          aria-label="Refresh"
          (click)="refresh()">
          <i class="fa-solid fa-rotate-right" [class.fa-spin]="loading"></i>
        </button>
      </div>
    </ng-container>

    <ng-template #plainSelect>
      <ng-container *ngTemplateOutlet="selectTemplate"></ng-container>
    </ng-template>

    <ng-template #selectTemplate>
      <ng-select
        [class.iwb-template-select]="usesTemplateSelectStyle"
        [items]="items"
        [loading]="loading"
        [bindValue]="bindValue"
        [multiple]="multiple"
        [virtualScroll]="true"
        [placeholder]="placeholder"
        [searchable]="true"
        [closeOnSelect]="!multiple"
        [typeahead]="search$"
        [formControl]="formControlAny"
        (scrollToEnd)="onScrollEnd()">
        <ng-template ng-label-tmp let-item="item" let-clear="clear">
          <span class="ng-value-label paginated-tmpl-label">
            {{ resolveLabel(item) }}
          </span>
          <span
            *ngIf="multiple"
            class="ng-value-icon right paginated-tmpl-clear"
            aria-hidden="true"
            (click)="clear(item); $event.stopPropagation()">
            ×
          </span>
        </ng-template>
        <ng-template ng-option-tmp let-item="item">
          {{ resolveOption(item) }}
        </ng-template>
      </ng-select>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginatedTemplateDropdownComponent
  extends FieldType
  implements OnInit, OnDestroy
{
  items: Array<Record<string, unknown>> = [];
  loading = false;
  search$ = new Subject<string>();

  private page = 0;
  private totalPages = 1;
  private lastSearchTerm = '';
  private destroy$ = new Subject<void>();

  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {
    super();
  }

  // ---------- props passthrough ----------
  get bindValue(): any {
    return this.field.props?.['bindValue'] as string | undefined;
  }
  get multiple(): boolean {
    return !!this.field.props?.['multiple'];
  }
  get placeholder(): string {
    return (this.field.props?.placeholder as string) ?? '';
  }
  get refreshable(): boolean {
    return !!this.field.props?.['refreshable'];
  }
  get usesTemplateSelectStyle(): boolean {
    return (
      this.refreshable ||
      String(this.field.props?.['selectClass'] ?? '')
        .split(/\s+/)
        .includes('iwb-template-select')
    );
  }
  /** ng-select expects a FormControl<any>; Formly's typing is stricter. */
  get formControlAny(): any {
    return this.formControl as unknown;
  }

  // ---------- lifecycle ----------
  ngOnInit(): void {
    // Pre-load items provided by the host (e.g. records fetched for the
    // already-saved values so chips render with proper labels on first paint).
    const preloaded = this.field.props?.['options'];
    if (Array.isArray(preloaded) && preloaded.length > 0) {
      this.items = preloaded as Array<Record<string, unknown>>;
    }

    // Initial page fetch (also seeds the dropdown when the user first opens it).
    this.fetchPage(0, '');

    // Typeahead — debounced, distinct, replaces the items array each time.
    this.search$
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(term => {
        this.lastSearchTerm = term ?? '';
        this.fetchPage(0, this.lastSearchTerm);
      });

    // Pick up later changes to `props.options` (host calls inject after
    // an async prefetch resolves) so saved chips upgrade once data arrives.
    this.field.options?.fieldChanges
      ?.pipe(takeUntil(this.destroy$))
      .subscribe((e: any) => {
        if (
          e?.field?.key === this.field.key &&
          e?.type === 'expressionChanges' &&
          e?.property === 'props.options' &&
          Array.isArray(e.value)
        ) {
          this.items = this.mergeById(this.items, e.value);
          this.cdr.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onScrollEnd(): void {
    if (this.page + 1 < this.totalPages) {
      this.fetchPage(this.page + 1, this.lastSearchTerm);
    }
  }

  refresh(): void {
    this.fetchPage(0, this.lastSearchTerm);
  }

  // ---------- label rendering ----------
  resolveLabel(item: Record<string, unknown> | null | undefined): string {
    if (!item) return '';
    const fn = this.field.props?.['labelFn'] as
      | ((i: Record<string, unknown>) => string)
      | undefined;
    if (typeof fn === 'function') return fn(item);
    const key = this.field.props?.['bindLabel'] as string | undefined;
    return key ? String(item?.[key] ?? '') : '';
  }

  resolveOption(item: Record<string, unknown> | null | undefined): string {
    if (!item) return '';
    const fn = (this.field.props?.['optionFn'] ??
      this.field.props?.['labelFn']) as
      | ((i: Record<string, unknown>) => string)
      | undefined;
    if (typeof fn === 'function') return fn(item);
    return this.resolveLabel(item);
  }

  // ---------- fetch ----------
  private fetchPage(page: number, searchTerm: string): void {
    const dataset = this.field.props?.['dataset'] as
      | Record<string, any>
      | undefined;
    if (!dataset?.['url']) return;

    let url = dataset['url'] as string;
    if (this.field.props?.['useBaseUrl']) {
      url = `${environment.apiGatewayBaseUrl}${url}`;
    }

    const sizeParam = dataset['params']?.size ?? 50;
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(sizeParam));
    Object.entries(dataset['params'] ?? {}).forEach(([key, value]) => {
      if (['page', 'size'].includes(key) || value == null || value === '') {
        return;
      }
      params = params.set(key, String(value));
    });
    if (searchTerm) {
      const searchField = (dataset['searchField'] as string) ?? 'searchTerm';
      params = params.set(searchField, searchTerm);
    }
    if (dataset['sort']) params = params.set('sort', String(dataset['sort']));
    if (dataset['sortDirection']) {
      params = params.set('sortDirection', String(dataset['sortDirection']));
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.http
      .get<unknown>(url, { params })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.loading = false;
          const dataField = dataset['dataField'] as string | undefined;
          const data = dataField ? this.resolvePath(res, dataField) : res;
          const content = this.filterItems(this.coerceContent(data));

          if (page === 0) {
            // Replace, but keep any preloaded items not in the new page so
            // existing chips don't disappear on the first search.
            this.items = this.mergeById(content, this.items);
          } else {
            this.items = this.mergeById(this.items, content);
          }

          this.page = this.numberOr(data, 'number', page);
          this.totalPages = this.numberOr(data, 'totalPages', 1);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  // ---------- helpers ----------
  private resolvePath(obj: unknown, path: string): unknown {
    return path
      .split('.')
      .reduce<any>((acc, key) => (acc == null ? acc : acc[key]), obj);
  }

  private coerceContent(data: unknown): Array<Record<string, unknown>> {
    if (Array.isArray(data)) return data as Array<Record<string, unknown>>;
    const content = (data as { content?: unknown })?.content;
    return Array.isArray(content)
      ? (content as Array<Record<string, unknown>>)
      : [];
  }

  private filterItems(
    items: Array<Record<string, unknown>>
  ): Array<Record<string, unknown>> {
    const itemFilter = this.field.props?.['itemFilter'] as
      | ((item: Record<string, unknown>) => boolean)
      | undefined;
    return typeof itemFilter === 'function'
      ? items.filter(item => itemFilter(item))
      : items;
  }

  private numberOr(data: unknown, key: string, fallback: number): number {
    const v = (data as Record<string, unknown> | null | undefined)?.[key];
    return typeof v === 'number' ? v : fallback;
  }

  /** Merge two arrays of records, de-duplicated by `bindValue` (or `id`). */
  private mergeById(
    primary: Array<Record<string, unknown>>,
    secondary: Array<Record<string, unknown>>
  ): Array<Record<string, unknown>> {
    const key = this.bindValue ?? 'id';
    const seen = new Set<unknown>();
    const out: Array<Record<string, unknown>> = [];
    for (const arr of [primary, secondary]) {
      for (const item of arr) {
        const id = item?.[key];
        if (id !== undefined && !seen.has(id)) {
          seen.add(id);
          out.push(item);
        }
      }
    }
    return out;
  }
}
